import React, { useContext, useEffect, useReducer, useState } from "react";
import logo from "../../assets/images/logo.svg";
import InputField from "../../components/InputField";
import { AuthContext } from "../../context/AuthContext";
import Navbar from "../../components/Navbar";
import { ToastContainer, toast, Bounce } from "react-toastify";
import { useNavigate } from "react-router-dom";

const inputs = [
  {
    name: "Email",
    label: "Email",
    required: true,
    id: "email",
    placeholder: "Enter your email address",
    type: "email",
  },
  {
    name: "Password",
    id: "password",
    required: true,
    label: "Password",
    placeholder: "Enter your password",
    type: "password",
  },
];

const changePasswordInputs = [
  {
    name: "Password",
    id: "oldPassword",
    required: true,
    label: "Password",
    placeholder: "Enter your password",
    type: "password",
  },
  {
    name: "New Password",
    id: "newPassword",
    required: true,
    label: "New Password",
    placeholder: "Enter new password",
    type: "password",
  },
];

const initialState = {
  formData: {},
  errors: {},
};

const ActionTypes = {
  SET_FIELD_VALUE: "SET_FIELD_VALUE",
  RESET_FORM: "RESET_FORM",
  SUBMIT_FORM: "SUBMIT_FORM",
};

const Reducer = (state = initialState, action) => {
  switch (action.type) {
    case ActionTypes.SET_FIELD_VALUE:
      return {
        ...state,
        formData: {
          ...state.formData,
          ...action.payload,
        },
      };
    case ActionTypes.RESET_FORM:
      return {
        ...state,
        formData: {},
        errors: {},
      };
    case ActionTypes.SUBMIT_FORM:
      return {
        ...state,
        ...action.payload,
      };
    default:
      return state;
  }
};

const Login = ({ passwordChange }) => {
  const [state, dispatch] = useReducer(Reducer, initialState);
  const { loginAction, updatePassword, isAuthenticated } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [inputType, setInputTypes] = useState([...inputs]);
  const [showBack, setShowBack] = useState(false);
  const [passwordUpdate, setPasswordUpdate] = useState(false);
  const navigate = useNavigate();

  // Check for redirect after login
  useEffect(() => {
    // If the user is already authenticated, check for redirect
    if (isAuthenticated) {
      const redirectPath = localStorage.getItem('afterLoginRedirect');
      if (redirectPath) {
        localStorage.removeItem('afterLoginRedirect');
        navigate(redirectPath);
      } else {
        // Default redirect to dashboard if no specific redirect path is saved
        navigate('/dashboard');
      }
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (passwordChange) {
      setInputTypes([...changePasswordInputs]);
      setShowBack(true);
    }
  }, [passwordChange]);

  const validateForm = (fields) => {
    let errors = {};
    fields.forEach((field) => {
      if (field.required && !state.formData[field.id]) {
        errors[field.id] = `${field.name} is required.`;
      }
    });
    dispatch({
      type: ActionTypes.SUBMIT_FORM,
      payload: { "errors": errors ? { ...errors } : {} },
    });
    return Object.values(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    if (!showBack) {
      if (!validateForm(inputs)) {
        setLoading(false);
        return;
      }
    } else {
      if (!validateForm(changePasswordInputs)) {
        setLoading(false);
        return;
      }
    }

    if (showBack) {
      try {
        const res = await updatePassword(state.formData);
        if (res) {
          setPasswordUpdate(true);
        }
        setLoading(false);
      } catch (err) {
        console.error('Error updating password:', err);
        setLoading(false);
        toast.error("Failed to update password. Please try again.", {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
          transition: Bounce,
        });
      }
    } else {
      try {
        await loginAction(state.formData);
        
        // Check if there's a redirect path stored
        const redirectPath = localStorage.getItem('afterLoginRedirect');
        if (redirectPath) {
          localStorage.removeItem('afterLoginRedirect');
          navigate(redirectPath);
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Login error:', err);
        toast.error("Invalid Email or Password", {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
          transition: Bounce,
        });
        setLoading(false);
      }
    }
  };

  const handleChange = (e) => {
    dispatch({
      type: ActionTypes.SET_FIELD_VALUE,
      payload: {
        [e.target.id]: e.target.value,
      },
    });
  };

  // Password change modal content
  if (passwordChange) {
    return (
      <form
        onSubmit={handleSubmit}
        className="flex flex-col justify-center items-center w-full max-w-md p-6"
      >
        {passwordUpdate ? (
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900">Password Updated</h2>
            <p className="text-gray-500 mt-2">Your password has been changed successfully.</p>
          </div>
        ) : (
          <>
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Update Password</h2>
            <div className="w-full space-y-4">
              {inputType.map((input, index) => (
                <InputField
                  key={`login-input-${index}`}
                  onChange={handleChange}
                  error={state.errors[input.id]}
                  input={input}
                />
              ))}
            </div>
            {state.errors.global && (
              <p className="mt-4 text-sm text-red-600 text-center">{state.errors.global}</p>
            )}
            <button
              type="submit"
              disabled={loading}
              className="mt-6 w-full py-2.5 px-4 bg-slate-900 text-white font-medium rounded-lg hover:bg-slate-800 transition-colors disabled:opacity-50"
            >
              {loading ? "Updating..." : "Update Password"}
            </button>
          </>
        )}
        <ToastContainer />
      </form>
    );
  }

  // Main login page
  return (
    <>
      <Navbar />
      <div 
        className="min-h-screen flex items-center justify-center pt-20 pb-10 px-4"
        style={{ backgroundColor: '#f8fafc' }}
      >
        <div style={{ width: '100%', maxWidth: '420px' }}>
          {/* Logo */}
          <div className="text-center mb-6">
            <img src={logo} alt="Clinica AI" className="h-10 mx-auto" />
          </div>

          {/* Login Card */}
          <div 
            className="rounded-xl"
            style={{ 
              backgroundColor: '#ffffff',
              border: '1px solid #e2e8f0',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)',
              padding: '2.5rem'
            }}
          >
            <h2 
              className="text-2xl font-semibold text-center mb-2"
              style={{ color: '#0f172a' }}
            >
              Sign in
            </h2>
            <p 
              className="text-center mb-6 text-sm"
              style={{ color: '#64748b' }}
            >
              Enter your credentials to access your account
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
              {inputType.map((input, index) => (
                <InputField
                  key={`login-input-${index}`}
                  onChange={handleChange}
                  error={state.errors[input.id]}
                  input={input}
                />
              ))}

              {state.errors.global && (
                <p className="text-sm text-red-600 text-center">{state.errors.global}</p>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full py-2.5 px-4 font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed mt-6"
                style={{ 
                  backgroundColor: '#0f172a', 
                  color: '#ffffff'
                }}
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Signing in...
                  </span>
                ) : (
                  "Sign in"
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
      <ToastContainer />
    </>
  );
};

export default Login;
